import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Search, Filter, Heart, Star, Users, TrendingUp,
  MapPin, Sparkles, Brain, Zap, Target, Eye,
  Grid3X3, List, RotateCcw, Bookmark
} from 'lucide-react';

import {
  AdvancedFilters,
  CompatibilityScore,
  EnhancedProfileCard,
  SmartBrowseHistory
} from './AdvancedMatchingComponents';

const MatchingDiscoveryPage = () => {
  const [currentFilters, setCurrentFilters] = useState({});
  const [profiles, setProfiles] = useState([]);
  const [viewedProfiles, setViewedProfiles] = useState([]);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('compatibility');
  const [bookmarkedProfiles, setBookmarkedProfiles] = useState([]);

  // Sample profiles data
  const sampleProfiles = [
    {
      id: 1,
      name: 'Sarah',
      age: 28,
      location: 'New York',
      distance: 3,
      photos: [
        'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400',
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400'
      ],
      bio: 'Adventure seeker who loves hiking, photography, and discovering new coffee shops. Looking for someone to explore the city with and share meaningful conversations over great food.',
      occupation: 'Graphic Designer',
      education: 'Bachelor\'s in Fine Arts',
      interests: ['Photography', 'Travel', 'Coffee', 'Hiking', 'Art'],
      mutualInterests: ['Photography', 'Travel', 'Coffee'],
      compatibilityScore: 87,
      lastActive: '2 hours ago',
      lookingFor: 'Serious relationship with someone who shares my love for creativity and adventure'
    },
    {
      id: 2,
      name: 'Emma',
      age: 26,
      location: 'Brooklyn',
      distance: 8,
      photos: [
        'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400',
        'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400'
      ],
      bio: 'Fitness enthusiast by day, foodie by night. I believe in living life to the fullest and always up for trying new restaurants or hitting a workout class together.',
      occupation: 'Marketing Manager',
      education: 'Master\'s in Business',
      interests: ['Fitness', 'Cooking', 'Marketing', 'Yoga', 'Wine'],
      mutualInterests: ['Fitness', 'Cooking'],
      compatibilityScore: 74,
      lastActive: '1 day ago',
      lookingFor: 'Someone who values health, good food, and can make me laugh'
    },
    {
      id: 3,
      name: 'Olivia',
      age: 30,
      location: 'Manhattan',
      distance: 5,
      photos: [
        'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400',
        'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400'
      ],
      bio: 'Book lover and weekend warrior. When I\'m not diving into a good novel, you can find me planning my next travel adventure or trying out new recipes.',
      occupation: 'Software Engineer',
      education: 'Master\'s in Computer Science',
      interests: ['Reading', 'Technology', 'Travel', 'Cooking', 'Music'],
      mutualInterests: ['Reading', 'Technology', 'Travel'],
      compatibilityScore: 91,
      lastActive: '30 minutes ago',
      lookingFor: 'A genuine connection with someone who loves deep conversations and new experiences'
    },
    {
      id: 4,
      name: 'Sophia',
      age: 27,
      location: 'Queens',
      distance: 12,
      photos: [
        'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=400'
      ],
      bio: 'Artist with a passion for sustainable living and making the world a better place. Looking for someone who shares my values and loves creativity.',
      occupation: 'Environmental Artist',
      education: 'Bachelor\'s in Environmental Science',
      interests: ['Art', 'Environment', 'Sustainability', 'Music', 'Yoga'],
      mutualInterests: ['Art', 'Music'],
      compatibilityScore: 68,
      lastActive: '5 hours ago',
      lookingFor: 'Meaningful relationship with someone who cares about our planet'
    }
  ];

  useEffect(() => {
    // Simulate loading profiles
    setIsLoading(true);
    setTimeout(() => {
      setProfiles(sampleProfiles);
      setIsLoading(false);
    }, 1000);

    // Load viewed profiles from localStorage
    const savedViewed = localStorage.getItem('viewedProfiles');
    if (savedViewed) {
      setViewedProfiles(JSON.parse(savedViewed));
    }

    // Load bookmarked profiles
    const savedBookmarks = localStorage.getItem('bookmarkedProfiles');
    if (savedBookmarks) {
      setBookmarkedProfiles(JSON.parse(savedBookmarks));
    }
  }, []);

  const handleFiltersChange = (filters) => {
    setCurrentFilters(filters);
    setIsLoading(true);
    
    // Simulate API call with filters
    setTimeout(() => {
      // Filter logic would go here
      setIsLoading(false);
    }, 800);
  };

  const handleLike = (profileId) => {
    console.log('Liked profile:', profileId);
    // Add to viewed profiles
    const profile = profiles.find(p => p.id === profileId);
    if (profile && !viewedProfiles.some(v => v.id === profileId)) {
      const updated = [...viewedProfiles, { 
        id: profile.id, 
        name: profile.name, 
        photo: profile.photos[0] 
      }];
      setViewedProfiles(updated);
      localStorage.setItem('viewedProfiles', JSON.stringify(updated));
    }
  };

  const handlePass = (profileId) => {
    console.log('Passed profile:', profileId);
    // Add to viewed profiles
    const profile = profiles.find(p => p.id === profileId);
    if (profile && !viewedProfiles.some(v => v.id === profileId)) {
      const updated = [...viewedProfiles, { 
        id: profile.id, 
        name: profile.name, 
        photo: profile.photos[0] 
      }];
      setViewedProfiles(updated);
      localStorage.setItem('viewedProfiles', JSON.stringify(updated));
    }
  };

  const handleBookmark = (profileId) => {
    const profile = profiles.find(p => p.id === profileId);
    if (profile) {
      const updated = bookmarkedProfiles.includes(profileId)
        ? bookmarkedProfiles.filter(id => id !== profileId)
        : [...bookmarkedProfiles, profileId];
      setBookmarkedProfiles(updated);
      localStorage.setItem('bookmarkedProfiles', JSON.stringify(updated));
    }
  };

  const handleRevisitProfile = (profileId) => {
    console.log('Revisiting profile:', profileId);
    // Scroll to profile or open detailed view
  };

  const clearHistory = () => {
    setViewedProfiles([]);
    localStorage.removeItem('viewedProfiles');
  };

  const sortProfiles = (profiles) => {
    switch (sortBy) {
      case 'compatibility':
        return [...profiles].sort((a, b) => b.compatibilityScore - a.compatibilityScore);
      case 'distance':
        return [...profiles].sort((a, b) => a.distance - b.distance);
      case 'lastActive':
        return [...profiles].sort((a, b) => new Date(b.lastActive) - new Date(a.lastActive));
      case 'age':
        return [...profiles].sort((a, b) => a.age - b.age);
      default:
        return profiles;
    }
  };

  const filteredAndSortedProfiles = sortProfiles(profiles);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                <Brain className="w-6 h-6 mr-2 text-rose-500" />
                Smart Matching
              </h1>
              <p className="text-sm text-gray-600">
                AI-powered compatibility • {profiles.length} profiles nearby
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {/* Sort Options */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:border-rose-500 focus:ring-2 focus:ring-rose-200 text-sm"
              >
                <option value="compatibility">Best Match</option>
                <option value="distance">Nearest</option>
                <option value="lastActive">Recently Active</option>
                <option value="age">Age</option>
              </select>

              {/* View Mode */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="rounded-none border-0"
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-none border-0"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <AdvancedFilters
              onFiltersChange={handleFiltersChange}
              initialFilters={currentFilters}
            />

            {/* Browse History */}
            {viewedProfiles.length > 0 && (
              <SmartBrowseHistory
                viewedProfiles={viewedProfiles}
                onRevisit={handleRevisitProfile}
                onClearHistory={clearHistory}
              />
            )}

            {/* Match Statistics */}
            <Card className="bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-rose-800 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Your Success Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-rose-700">73%</div>
                    <div className="text-xs text-rose-600">Match Success Rate</div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="text-center">
                      <div className="font-semibold text-gray-800">24</div>
                      <div className="text-gray-600">Mutual Likes</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-gray-800">15</div>
                      <div className="text-gray-600">Conversations</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {/* Active Filters Display */}
            {Object.keys(currentFilters).length > 0 && (
              <Card className="mb-6 bg-blue-50 border-blue-200">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Filter className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Active Filters:</span>
                      <div className="flex flex-wrap gap-1">
                        {currentFilters.interests?.length > 0 && (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">
                            {currentFilters.interests.length} interests
                          </Badge>
                        )}
                        {currentFilters.ageRange && (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">
                            Ages {currentFilters.ageRange[0]}-{currentFilters.ageRange[1]}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCurrentFilters({});
                        handleFiltersChange({});
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Clear All
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-12">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="inline-block"
                >
                  <Brain className="w-8 h-8 text-rose-500" />
                </motion.div>
                <p className="text-gray-600 mt-4">Finding your perfect matches...</p>
              </div>
            )}

            {/* Profiles Grid/List */}
            {!isLoading && (
              <AnimatePresence mode="wait">
                {viewMode === 'cards' ? (
                  <motion.div
                    key="cards"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="grid md:grid-cols-2 gap-6"
                  >
                    {filteredAndSortedProfiles.map((profile, index) => (
                      <motion.div
                        key={profile.id}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <EnhancedProfileCard
                          profile={profile}
                          onLike={handleLike}
                          onPass={handlePass}
                          onBookmark={handleBookmark}
                          showCompatibility={true}
                          showMutualInterests={true}
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <motion.div
                    key="list"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-4"
                  >
                    {filteredAndSortedProfiles.map((profile, index) => (
                      <motion.div
                        key={profile.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card className="hover:shadow-lg transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-center space-x-4">
                              <img
                                src={profile.photos[0]}
                                alt={profile.name}
                                className="w-16 h-16 object-cover rounded-lg"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h3 className="font-semibold text-gray-800">
                                      {profile.name}, {profile.age}
                                    </h3>
                                    <div className="flex items-center text-sm text-gray-500">
                                      <MapPin className="w-3 h-3 mr-1" />
                                      {profile.distance}km away
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className={`text-sm font-semibold ${
                                      profile.compatibilityScore >= 80 ? 'text-green-600' :
                                      profile.compatibilityScore >= 60 ? 'text-blue-600' :
                                      'text-yellow-600'
                                    }`}>
                                      {profile.compatibilityScore}% Match
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {profile.mutualInterests.length} shared interests
                                    </div>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-600 mt-2 line-clamp-1">
                                  {profile.bio}
                                </p>
                              </div>
                              <div className="flex space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handlePass(profile.id)}
                                >
                                  Pass
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleLike(profile.id)}
                                  className="bg-rose-500 hover:bg-rose-600"
                                >
                                  <Heart className="w-4 h-4 mr-1" />
                                  Like
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            )}

            {/* No Results */}
            {!isLoading && filteredAndSortedProfiles.length === 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  No matches found
                </h3>
                <p className="text-gray-600 mb-6">
                  Try adjusting your filters to see more profiles
                </p>
                <Button
                  onClick={() => {
                    setCurrentFilters({});
                    handleFiltersChange({});
                  }}
                  className="bg-rose-500 hover:bg-rose-600"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset Filters
                </Button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingDiscoveryPage;